/**
 * Typst generation against the 2026 course repository ("course sheets").
 *
 * Studio's own Typst build writes one self-contained `sheet.typ` against its own
 * `prog1.typ`. A course sheet is written the way the course repository lays its sheets
 * out instead, so that what Studio previews is byte for byte what the course CI builds
 * and what tutors later find in GitLab:
 *
 *   sheets/tutorial/A/tutorial-A.typ          the sheet: BUILD lines, import, show rule,
 *                                             headings and one `#include` per exercise
 *   sheets/tutorial/A/exercises/<slug>.typ    one file per exercise
 *   sheets/tutorial/A/embeds/<slug>.pdf       LaTeX exercises, frozen as PDF (staged by
 *                                             the server; this module only names them)
 *
 * The solution and marking versions are not produced by stripping text, as Studio's
 * own build does, but by compiling the same files again with `--input solution=true` or
 * `--input marking=true` — the headers decide what to show. That is why every solution
 * in a body has to arrive as `#solution[…]`, the name the headers know.
 *
 * Pure: no file system, no node imports. The server stages the plan and compiles it.
 */

import type { SheetDoc, SheetItem, SheetKind, SheetMeta } from './index.js';
import { unescapeTex } from './textutil.js';
import { escapeTypstText, latexPlainToTypst, stripLatexComments, typstStudentScope } from './typst.js';
import { findTypstBlocks } from './typstscan.js';
import { HIDE_POINTS_MARKER, ICONS_MARKER, iconAlt } from './difficultyIcons.js';

/** Around a course sheet's own Typst in its main file (`SheetMeta.typstPreamble`). */
export const PREAMBLE_START = '// studio: preamble';
export const PREAMBLE_END = '// studio: end preamble';

/** `meta.headerName` of a course sheet. Not a LaTeX header pack: nothing `\input`s it. */
export const COURSE_PACK = 'course/prog1-2026';
export const COURSE_PACK_LABEL = 'Prog1 2026 (course template)';

export function isCourseSheet(meta: { headerName?: string } | undefined | null): boolean {
  return meta?.headerName === COURSE_PACK;
}

/** The sheet templates of `headers/sheets/templates.typ` that Studio can generate. */
export type CourseFunction =
  | 'tutorial-sheet'
  | 'exercise-sheet'
  | 'exam-preparation-sheet'
  | 'extra-tutorial-sheet'
  | 'rep-tutorial-sheet'
  | 'exam-task-sheet'
  | 'explanation-sheet'
  | 'knobel-sheet';

export const COURSE_FUNCTIONS: readonly CourseFunction[] = [
  'tutorial-sheet',
  'exercise-sheet',
  'exam-preparation-sheet',
  'extra-tutorial-sheet',
  'rep-tutorial-sheet',
  'exam-task-sheet',
  'explanation-sheet',
  'knobel-sheet',
];

/*
 * Studio's sheet kinds are older than the course templates and coarser. Two of them
 * have no template of their own yet: card sheets are set as an explanation, and a mock
 * exam — which the course builds with `exam.template`, a different document shape
 * altogether — as an exam preparation sheet. `buildCoursePlan` warns about both.
 */
const FUNCTION_BY_KIND: Record<SheetKind, CourseFunction> = {
  tutorial: 'tutorial-sheet',
  exercise: 'exercise-sheet',
  'exam-prep': 'exam-preparation-sheet',
  other: 'explanation-sheet',
  cards: 'explanation-sheet',
  'mock-exam': 'exam-preparation-sheet',
};

/** The template a sheet is written against: the one chosen for it, else its kind's. */
export function courseFunctionFor(meta: SheetMeta): CourseFunction {
  if (meta.courseFunction && COURSE_FUNCTIONS.includes(meta.courseFunction)) return meta.courseFunction;
  return FUNCTION_BY_KIND[meta.kind] ?? 'tutorial-sheet';
}

const BASE_DIRS: Record<CourseFunction, string> = {
  'tutorial-sheet': 'sheets/tutorial',
  'exercise-sheet': 'sheets/exercise',
  'exam-preparation-sheet': 'sheets/exam_preparation',
  'extra-tutorial-sheet': 'sheets/tutorial_extra',
  'rep-tutorial-sheet': 'sheets/tutorial_rep',
  'exam-task-sheet': 'sheets/exam_tasks',
  'explanation-sheet': 'sheets/additional',
  'knobel-sheet': 'sheets/knobel',
};

/** Where in the course repository a sheet of this template lives. */
export function courseBaseDir(fn: CourseFunction): string {
  return BASE_DIRS[fn];
}

/* ------------------------------------------------------------------ names */

/**
 * Lowercase ASCII with dashes, or '' when nothing is left.
 *
 * German titles are common, and stripping the diacritic alone turns "Übung" into
 * "ubung"; the umlauts are spelled out first the way a German reader would.
 */
function slugBase(text: string, max: number): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/, '');
}

const SLUG_MAX = 48;

/**
 * A file-name slug for `text`, unique within `used`, which it is added to.
 *
 * Never empty — a title of nothing but punctuation still needs a file — and the suffix
 * that makes it unique stays inside the length limit rather than being appended to it.
 */
export function courseSlug(text: string, used: Set<string>): string {
  const base = slugBase(text, SLUG_MAX) || 'exercise';
  let slug = base;
  for (let n = 2; used.has(slug); n++) {
    const suffix = `-${n}`;
    slug = base.slice(0, SLUG_MAX - suffix.length).replace(/-+$/, '') + suffix;
  }
  used.add(slug);
  return slug;
}

/**
 * The sheet's directory name inside its base directory: `sheets/tutorial/<this>/`.
 *
 * The label is what the course uses (`sheets/tutorial/A/`), but it is free text in
 * Studio. Anything that is not a plain file-name character is dropped, so no label can
 * produce `..`, a slash, or a name that a shell or `build.py` reads differently.
 */
export function courseDirName(meta: SheetMeta, sheetId: string): string {
  const label = (meta.label ?? '')
    .trim()
    // transliterated first, as slugBase does, but keeping the case: `Übung 3` → `Uebung-3`, not `bung-3`
    .replace(/[ÄÖÜäöüß]/g, (c) => ({ Ä: 'Ae', Ö: 'Oe', Ü: 'Ue', ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' })[c] ?? c)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .slice(0, 40)
    .replace(/^-+|-+$/g, '');
  if (label) return label;
  const title = slugBase(meta.title ?? '', 40);
  if (title) return title;
  const id = sheetId.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8);
  return id ? `sheet-${id}` : 'sheet';
}

/* ------------------------------------------------------------------ bodies */

/**
 * A Typst body written for Studio's template, as a body for the course headers.
 *
 * Studio's template calls the solution `answer`; the course headers call it `solution`
 * and show it under `--input solution=true` (and `marking=true`). The bracket content
 * is kept as written — only the name changes — and the scanner is the one the student
 * stripping uses, so a `]` in an OCaml example inside the solution does not end it.
 *
 * With `includeSolution: false` (an item whose solution was switched off on the sheet)
 * the body is the student version in every build, as in Studio's own: its solution and
 * marking blocks are dropped from the source itself, and the rest is scoped to the
 * student flags (`typstStudentScope`), because `#answer-space`, `#solution-box`, the
 * ticked boxes and `is-solution()` would otherwise show their solutions in the solutions
 * build. Marking notes used to stay, but the headers' marking build is a solutions build
 * too (`is-solution()` is true in it), so they could not stay without the rest.
 */
export function toCourseBody(body: string, opts: { includeSolution?: boolean } = {}): string {
  const drop = opts.includeSolution === false;
  if (drop) {
    const student = dropSolutions(body);
    return student.trim() ? typstStudentScope(student, 'prog1:flags') : student;
  }
  return dropSolutions(body, false);
}

/** `toCourseBody` without the scope: the solutions renamed, or dropped with `drop`. */
function dropSolutions(body: string, drop = true): string {
  let out = '';
  let i = 0;
  for (const block of findTypstBlocks(body, ['answer', 'solution', 'marking'])) {
    // listed so the scan steps over a marking block whole, not to change it — unless
    // the student version is wanted
    if (block.name === 'marking' && !drop) continue;
    out += body.slice(i, block.start);
    if (block.close < 0) {
      /*
       * An unterminated `#answer[` is dropped to the end, the same rule as
       * `stripTypstAnswer`: a half-parsed solution must never reach a student sheet.
       * An unterminated `#solution[` is already in the headers' own terms and is left
       * for typst to report, unless solutions are being dropped anyway.
       */
      if (block.name === 'answer' || drop) return out.trimEnd() + '\n';
      return out + body.slice(block.start);
    }
    if (!drop) {
      out += block.name === 'answer' ? '#solution' + body.slice(block.open, block.close + 1) : body.slice(block.start, block.close + 1);
    }
    i = block.close + 1;
  }
  return out + body.slice(i);
}

/**
 * Definitions a body needs because it still calls something only Studio's template has.
 *
 * `hint-box` is the one such name in use; the headers call it `hint`. Defined rather
 * than rewritten so the body stays exactly as its author wrote it.
 */
export function courseShim(body: string): string {
  const lines: string[] = [];
  if (/(?<![\w-])hint-box\s*[([]/.test(body)) lines.push('#let hint-box(body) = hint(body)');
  return lines.join('\n');
}

/*
 * A PDF placed page by page, at its natural size unless that is wider than the column.
 *
 * Emitted into every file that uses it rather than kept in one place: `image()` resolves
 * a relative path against the file the call is *written* in, so a helper shared from
 * the main file would look for an exercise file's `../embeds/…` next to the main file.
 * Each page is its own unbreakable block, so typst can break between pages but never
 * through one.
 */
const EMBED_HELPER = [
  '#let studio-embed(path, pages) = for p in range(1, pages + 1) {',
  '  block(width: 100%, breakable: false, layout(size => {',
  '    let nat = measure(image(path, page: p))',
  '    image(path, page: p, width: calc.min(nat.width, size.width))',
  '  }))',
  '}',
].join('\n');

/* ------------------------------------------------------------------- plan */

export interface CourseItemInput {
  /** exercise items: plain text, escaped; null or '' → no title */
  title?: string | null;
  /** a finite number → `points: n`; anything else leaves the headers' default */
  points?: number | null;
  /** a whole number ≥ 1, clamped to 4 → `difficulty: n`; anything else → none */
  difficulty?: number | null;
  /**
   * What the body is. A Typst body is the *raw* Studio body, `#answer[…]` and all: the
   * generator applies `toCourseBody` itself. Embed paths are relative to the sheet
   * directory, e.g. `embeds/<slug>.pdf`.
   */
  content:
    | { kind: 'typst'; body: string }
    | {
        kind: 'embed';
        student: { path: string; pages: number };
        solution?: { path: string; pages: number } | null;
      }
    | { kind: 'missing'; reason: string };
  includeSolution?: boolean;
  /**
   * The title as Typst markup, written as it is instead of escaping `title` — an archive
   * exercise's own title (`[Totally my type \<3]`). `title` stays the plain text the file
   * name is made from. Only set when the sheet does not override the title.
   */
  titleMarkup?: string | null;
  /**
   * The exercise is a block of the 2026 Typst archive (`a26/…`, Phase 8): a Typst body
   * written for `archive/2026/compat.typ`, which its file then imports (the course
   * repository has it, so the CI builds the file as Studio does). The block's own
   * `number:` is not written — the sheet numbers its exercises, and the headers file
   * subtask points under the counter, which `number:` does not advance — but kept as a
   * comment; its competencies are passed on.
   */
  archive?: {
    /** relative to `archive/2026` */
    path: string;
    /** 1-based block of the file */
    block: number;
    /** `number:` as written in the archive (`0`, `-1`), or null */
    number?: string | null;
    competencies?: readonly string[];
  } | null;
}

/** The import an archive block needs (= ARCHIVE_COMPAT_IMPORT of archive2026.ts, which imports this module). */
export const COURSE_ARCHIVE_IMPORT = '#import "/archive/2026/compat.typ": *';

/**
 * What `archive/2026/compat.typ` adds to the course headers for the archive's port of the
 * 2025 LaTeX macros. Studio's own template (`prog1.typ`) has none of it, and cannot import
 * `compat.typ` — that brings the course headers' `exercise`, counters and page set-up
 * along — so an archive body that uses one of these builds on a course sheet only.
 */
export const ARCHIVE_COMPAT_NAMES: readonly string[] = [
  'cetz',
  'fletcher',
  'fit-width',
  'enum-columns',
  'list-columns',
  'strlit',
  'explanation',
  'ex-label',
  'ex-ref',
  'cto',
];

/**
 * The names of `ARCHIVE_COMPAT_NAMES` a Typst body uses: called (`fit-width(`,
 * `#strlit[`), as a module (`fletcher.diagram`), or written with `#`. A bare word in the
 * prose ("an explanation") is not a use; `cto` written bare in math is missed.
 */
export function archiveCompatUses(body: string): string[] {
  const text = body.replace(/\/\/[^\n]*/g, '');
  return ARCHIVE_COMPAT_NAMES.filter((name) => {
    const n = name.replace(/-/g, '\\-');
    return new RegExp(`(?<![\\w-])${n}(?=[(\\[.])|#${n}(?![\\w-])`).test(text);
  });
}

/**
 * A Typst text split into what is math (`$…$`) and what is not. Math may hold code
 * (`$f := #set-of($x$)$`), whose own `$…$` would pair up wrongly with a plain regex and
 * read a paragraph of prose as math: `#name(…)`/`#name[…]` inside math goes to the code
 * side whole, brackets balanced.
 */
function splitMath(text: string): { math: string; code: string } {
  let math = '';
  let code = '';
  let inMath = false;
  const skipGroup = (from: number): number => {
    let depth = 0;
    let i = from;
    for (; i < text.length; i++) {
      const c = text[i]!;
      if (c === '\\') {
        i++;
        continue;
      }
      if (c === '(' || c === '[' || c === '{') depth++;
      else if (c === ')' || c === ']' || c === '}') {
        depth--;
        if (depth === 0) return i + 1;
      }
    }
    return i;
  };
  for (let i = 0; i < text.length; ) {
    const c = text[i]!;
    if (c === '\\') {
      const two = text.slice(i, i + 2);
      if (inMath) math += two;
      else code += two;
      i += 2;
      continue;
    }
    if (c === '$') {
      inMath = !inMath;
      math += '\n';
      code += ' ';
      i++;
      continue;
    }
    if (inMath && c === '#') {
      let j = i + 1;
      while (j < text.length && /[\w.-]/.test(text[j]!)) j++;
      while (j < text.length && (text[j] === '(' || text[j] === '[')) j = skipGroup(j);
      code += ` ${text.slice(i, j)} `;
      math += ' ';
      i = j;
      continue;
    }
    if (inMath) math += c;
    else code += c;
    i++;
  }
  return { math: math.trim(), code };
}

/**
 * The names of `names` a Typst body uses — the course headers' vocabulary an archive body
 * may lean on (`syntax-tree`, `dot-tree`, `derivation`, `wrt`, `cto`, …), which Studio's
 * own template does not have (Phase 8 review).
 *
 * Stricter than `archiveCompatUses` about prose, which a list of everyday words
 * (`section`, `colors`, `derivation`) needs: comments, raw text and one-line strings are
 * left out, and a name counts when written with `#`, or called, indexed or given content
 * (`name(`, `name.`, `name[`) right after code punctuation — `(`, `,`, `:`, `=`, `{`, `;`,
 * `+` — or at the start of a line; "derivation(s)" in a sentence is not a use. In math
 * (`$a cto b$`) a bare name without a hyphen counts, unless typst has it there itself
 * (`builtinInMath`: `$section$` is typst's §). A name the body defines itself
 * (`#let wrt = …`) is its own and never counts.
 */
export function typstNamesUsed(
  body: string,
  names: readonly string[],
  opts: { builtinInMath?: ReadonlySet<string> } = {},
): string[] {
  if (!names.length || !body) return [];
  const text = body
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`\n]*`/g, ' ')
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/(^|[^:\\])\/\/[^\n]*/g, '$1')
    .replace(/"(?:[^"\\\n]|\\.)*"/g, '""');
  const { math, code } = splitMath(text);
  const esc = (n: string) => n.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
  return names.filter((name) => {
    const n = esc(name);
    if (new RegExp(`(?:^|[^\\w-])let\\s+${n}(?![\\w-])`, 'm').test(text)) return false;
    if (new RegExp(`#${n}(?![\\w-])`).test(text)) return true;
    // `abs(x)` without `#` is typst's own where it has one: in math, or a call nested in one
    if (opts.builtinInMath?.has(name)) return false;
    if (new RegExp(`(?<=(?:^|[(,:={;+])[ \\t]*)${n}(?=[(\\[.])`, 'm').test(code)) return true;
    if (name.includes('-') || math === '') return false;
    return new RegExp(`(?<![\\w.])${n}(?![\\w])`).test(math);
  });
}

export interface CoursePlanInput {
  doc: SheetDoc;
  sheetId: string;
  /** per exercise/text item id; heading/pagebreak need no entry */
  items: Record<string, CourseItemInput>;
  /** sheet foot note (meta.footNote) when present: typst body or an embed */
  footNote?: { kind: 'typst'; body: string } | { kind: 'embed'; path: string; pages: number } | null;
  /** generated-at stamp for the header comment, injected for determinism */
  stamp?: string;
  /**
   * Names fixed by an earlier publish to the course repository (server/src/gitlab): the
   * sheet's directory, and each exercise's file slug by item id. A published sheet keeps
   * its directory when its number changes, and an exercise keeps its file when its title
   * changes — reordering or renaming then touches only the main file. Names that are not
   * plain file names are ignored.
   */
  pinned?: CoursePins;
  /**
   * The sheet's difficulty icons when they are not the template's camels: the set, and its
   * four files staged in the sheet's directory (`icons/level1.png` …), which a publish
   * commits beside it.
   */
  icons?: { set: string; files: string[] } | null;
}

export interface CoursePins {
  dir?: string | null;
  slugs?: Record<string, string>;
}

const PINNED_DIR = /^[A-Za-z0-9_-]{1,64}$/;
const PINNED_SLUG = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** A pinned directory name, if it is a plain one. */
export const pinnedDir = (pins: CoursePins | undefined | null): string | null =>
  pins?.dir && PINNED_DIR.test(pins.dir) ? pins.dir : null;

export interface CoursePlan {
  /** e.g. 'sheets/tutorial' */
  baseDir: string;
  /** e.g. 'A' */
  dir: string;
  /** main file name inside dir, e.g. 'tutorial-A.typ' */
  main: string;
  /** text files keyed by path relative to the sheet dir: main + 'exercises/<slug>.typ' */
  files: Record<string, string>;
  /** item id → file (relative to sheet dir) that holds it */
  itemFiles: Record<string, string>;
  /** for the main file: item id → [firstLine, lastLine] (1-based, inclusive) of its snippet */
  mainLines: Record<string, [number, number]>;
  /** BUILD variant names in the main file */
  buildNames: { solution: string; marking: string };
  warnings: { itemId: string | null; message: string }[];
}

/** `// BUILD <name>: <args>` — the pattern `scripts/build.py` reads (`BUILD_TAG`). */
export function parseBuildLines(src: string): { name: string; args: string }[] {
  const tag = /^\s*\/\/\s*BUILD\s+([^\s:]+)\s*:\s*(.*?)\s*$/gm;
  return [...src.matchAll(tag)].map((m) => ({ name: m[1]!, args: m[2]! }));
}

/** A Typst string literal. */
const str = (s: string): string => '"' + s.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';

/** One line of a `//` comment: a newline in an id would end the comment early. */
const oneLine = (s: string): string => s.replace(/[\r\n]+/g, ' ');

/** Whether text written in a plain field is Typst on purpose: it calls a function. */
const callsTypst = (s: string): boolean => /(^|[^\\])#[A-Za-z][\w-]*[([]/.test(s);

/** Leading blank lines and trailing whitespace go; indentation inside does not. */
const tidy = (body: string): string => body.replace(/^(?:[ \t]*\r?\n)+/, '').trimEnd();

const missingText = (reason: string): string => `#text(fill: red)[${escapeTypstText(reason)}]`;

/** An embed path relative to the sheet directory, as a path relative to `exercises/`. */
const fromExercises = (p: string): string => '../' + p.replace(/^(?:\.\/)+/, '');

/**
 * The exercise slugs of a sheet, by item id, in sheet order — exactly the names
 * `buildCoursePlan` gives the exercise files, so the server can name an exercise's
 * embeds after it before the plan exists. `titles` are the effective titles by item id;
 * `pinned` are names an earlier publish fixed (see `CoursePlanInput.pinned`).
 */
export function courseExerciseSlugs(
  doc: SheetDoc,
  titles: Record<string, string | null | undefined>,
  pinned?: Record<string, string>,
): Record<string, string> {
  const used = new Set<string>();
  const out: Record<string, string> = {};
  // pinned names first, so a new exercise never takes the file of a published one
  for (const item of doc.items) {
    const slug = item.kind === 'exercise' ? pinned?.[item.id] : undefined;
    if (slug && PINNED_SLUG.test(slug) && !used.has(slug)) {
      used.add(slug);
      out[item.id] = slug;
    }
  }
  for (const item of doc.items) {
    if (item.kind === 'exercise' && !(item.id in out)) out[item.id] = courseSlug(titles[item.id] ?? '', used);
  }
  return out;
}

/**
 * The `number:` argument, or null when the template takes none.
 *
 * Every template but one passes the number through `str()`, so a string prints exactly
 * what was typed. A plain number is written as an int because that is how the course's
 * own sheets read; one with a leading zero (`00`) stays a string, since as an int it
 * would print as `0`. `exam-task-sheet` is the exception: it prints the number as a
 * letter (`numbering("A", n)`), so its label has to become that letter's index.
 */
function numberArg(fn: CourseFunction, label: string, warn: (message: string) => void): string | null {
  if (fn === 'explanation-sheet') return null;
  const text = label.trim();
  if (fn === 'exam-task-sheet') {
    if (/^[A-Za-z]$/.test(text)) return String(text.toUpperCase().charCodeAt(0) - 64);
    if (/^[1-9]\d{0,5}$/.test(text)) return text;
    warn(`Exam task sheets are numbered by letter; “${label}” is not one, so the sheet is printed as A`);
    return '1';
  }
  if (!text) {
    warn('Sheet number is empty');
    return '""';
  }
  return /^(0|[1-9]\d{0,8})$/.test(text) ? text : str(text);
}

export function buildCoursePlan(input: CoursePlanInput): CoursePlan {
  const { doc, sheetId } = input;
  const meta = doc.meta;
  const warnings: CoursePlan['warnings'] = [];
  const warn = (itemId: string | null, message: string) => warnings.push({ itemId, message });

  const fn = courseFunctionFor(meta);
  if (!meta.courseFunction) {
    if (meta.kind === 'cards') warn(null, 'Card sheets have no course template yet; this one is set as an explanation sheet');
    if (meta.kind === 'mock-exam') warn(null, 'mock exams use exam.template; not generated yet');
  }
  const baseDir = courseBaseDir(fn);
  const dir = pinnedDir(input.pinned) ?? courseDirName(meta, sheetId);
  const stem = `${fn.replace(/-sheet$/, '')}-${dir}`;
  const main = `${stem}.typ`;
  const buildNames = { solution: `${stem}_solution`, marking: `${stem}_marking` };

  if ((meta.subtitle ?? '').trim()) warn(null, 'The 2026 template prints no subtitle');
  if (meta.difficultyIcons && !input.icons) warn(null, 'The difficulty icons could not be found; the sheet prints camels');
  else if (meta.coloredCamels === false && !input.icons) warn(null, 'The 2026 template always prints coloured camels');

  /* ---- the show rule */
  const args: string[] = [];
  const number = numberArg(fn, meta.label ?? '', (m) => warn(null, m));
  if (number !== null) args.push(`number: ${number}`);
  const title = (meta.title ?? '').trim();
  /*
   * As for the subtitle of Studio's own Typst build: plain text is escaped, so an e-mail
   * address in a title cannot become a reference, and only a title that calls a Typst
   * function on purpose is passed through as written.
   *
   * A sheet title is written for the LaTeX header first — every title from the corpus
   * is (`Bäume \& Mengen`), and a sheet made from one keeps it when switched to the
   * course pack — so its escapes are resolved first, as Studio's own Typst build does
   * for the title. Comments are not stripped: in a title field `50% off` means the words.
   */
  if (title) args.push(`title: [${callsTypst(title) ? title : escapeTypstText(unescapeTex(title))}]`);
  if (fn === 'knobel-sheet') args.push(`lang: ${str(meta.language === 'en' ? 'en' : 'de')}`);
  if (fn === 'explanation-sheet' && meta.language === 'de') args.push('lang: "de"');

  const lines: string[] = [
    `// BUILD ${buildNames.solution}: --input solution=true`,
    `// BUILD ${buildNames.marking}: --input marking=true`,
    `// Generated by Sheet Studio (sheet ${oneLine(sheetId)})${input.stamp ? `, ${oneLine(input.stamp)}` : ''}. ` +
      'Edit it in Studio: changes made here are overwritten on the next publish.',
    '',
    '#import "/headers/lib.typ": *',
  ];
  /** Index of the line after the import, where shim lines and the helper go once known. */
  const preambleAt = lines.length;
  lines.push('', `#show: ${fn}${args.length ? `.with(${args.join(', ')})` : ''}`);
  // the sheet's own Typst (layout settings), right after the template it adjusts
  const own = (meta.typstPreamble ?? '').replace(/\r\n/g, '\n').replace(/^\n+|\s+$/g, '');
  if (own) lines.push(PREAMBLE_START, ...own.split('\n'), PREAMBLE_END);
  const offset = Math.trunc(Number(meta.numberOffset) || 0);
  if (offset > 0) lines.push(`#ex-counter.update(${offset})`);
  /*
   * Other difficulty icons: the template's own hook, with the pictures beside the file, and
   * a marker naming the set so reading the file back knows it (coursesheet-parse.ts).
   */
  if (input.icons && input.icons.files.length === 4) {
    const images = input.icons.files.map(
      (f, i) => `image(${str(f)}, width: 1.6cm, height: 2em, fit: "contain", alt: ${str(iconAlt(i + 1))})`,
    );
    lines.push(`${ICONS_MARKER} ${oneLine(input.icons.set)}`, `#set-difficulty-icons((${images.join(', ')}))`);
  }
  // no points printed: every exercise file says `points: none` (leaving it out would add up the subtasks)
  if (meta.hidePoints) lines.push(HIDE_POINTS_MARKER);
  lines.push('');

  const mainLines: CoursePlan['mainLines'] = {};
  const files: Record<string, string> = {};
  const itemFiles: Record<string, string> = {};
  let mainNeedsEmbed = false;
  /** Every Typst body inlined into the main file, for its shim. */
  const inlined: string[] = [];

  /** Append a snippet (and a blank line after it) and record where it landed. */
  const emit = (itemId: string | null, snippet: string): void => {
    const first = lines.length + 1;
    lines.push(...snippet.split('\n'));
    if (itemId) mainLines[itemId] = [first, lines.length];
    lines.push('');
  };

  const headingText = (item: SheetItem): string => {
    const raw = (item.body ?? '').trim();
    const markup = item.markup ?? meta.markup ?? 'latex';
    if (markup === 'latex') return latexPlainToTypst(raw).trim();
    return callsTypst(raw) ? raw : escapeTypstText(raw);
  };

  /** A text block's content in the main file, or null when it prints nothing. */
  const textContent = (item: SheetItem): string | null => {
    const entry = input.items[item.id];
    /*
     * Without an entry from the server a Typst block is still its own body. A LaTeX one
     * needed compiling and was not; a block of nothing but LaTeX comments — a new
     * block's seed — prints nothing in LaTeX either, so it gets no marker.
     */
    if (!entry && (item.markup ?? meta.markup) !== 'typst' && !stripLatexComments(item.body ?? '').trim()) return null;
    const content: CourseItemInput['content'] = entry?.content ??
      ((item.markup ?? meta.markup) === 'typst'
        ? { kind: 'typst', body: item.body ?? '' }
        : { kind: 'missing', reason: 'This LaTeX block could not be compiled.' });
    if (content.kind === 'typst') {
      const body = tidy(toCourseBody(content.body, { includeSolution: entry?.includeSolution }));
      if (!body.trim()) return null;
      inlined.push(body);
      return body;
    }
    if (content.kind === 'embed') {
      mainNeedsEmbed = true;
      return `#studio-embed(${str(content.student.path)}, ${content.student.pages})`;
    }
    warn(item.id, content.reason);
    return missingText(content.reason);
  };

  const slugs = courseExerciseSlugs(
    doc,
    Object.fromEntries(Object.entries(input.items).map(([id, entry]) => [id, entry.title ?? null])),
    input.pinned?.slugs,
  );

  /*
   * The course numbers exercises with a counter, not with a printed string, so only an
   * override that is a plain number can be honoured: the counter is set one below it
   * and the exercise steps it. Anything else ("T3.x", "Bonus") has no counterpart.
   *
   * The headers also file each exercise's subtask points under its counter value, so
   * two exercises with one number would each print the sum of both. An override that
   * repeats a number already printed is therefore refused like the others; a number
   * repeated by counting on after an earlier override cannot be avoided without
   * renumbering, and is pointed out instead. The counter runs in the order the main
   * file includes the exercises: pinned ones come last.
   */
  const counterResets = new Map<string, number>();
  {
    const exercises = doc.items.filter((i) => i.kind === 'exercise');
    let counter = Math.max(offset, 0);
    const printed = new Set<number>();
    for (const item of [...exercises.filter((i) => !i.pinToBottom), ...exercises.filter((i) => i.pinToBottom)]) {
      const override = (item.numberOverride ?? '').trim();
      const wanted = /^\d+$/.test(override) ? Number(override) : NaN;
      if (Number.isSafeInteger(wanted) && wanted >= 1 && !printed.has(wanted)) {
        counterResets.set(item.id, wanted - 1);
        counter = wanted;
      } else {
        counter += 1;
        if (Number.isSafeInteger(wanted) && wanted >= 1) {
          warn(
            item.id,
            `Number override “${override}” repeats an earlier exercise's number, and the course template ` +
              `would add both exercises' points together; the exercise keeps its automatic number ${counter}`,
          );
        } else if (override) {
          warn(
            item.id,
            `Number override “${override}” cannot be expressed in the course template; the exercise keeps its automatic number`,
          );
        }
        if (printed.has(counter)) {
          warn(
            item.id,
            `This exercise is numbered ${counter} again after an earlier number override, and the course ` +
              'template would add both exercises\' points together; give it a number override of its own',
          );
        }
      }
      printed.add(counter);
    }
  }

  /** Writes the exercise's own file and returns its `#include` line(s) for the main file. */
  const exerciseSnippet = (item: SheetItem): string => {
    const rel = `exercises/${slugs[item.id]}.typ`;
    files[rel] = exerciseFile(item, input.items[item.id], (m) => warn(item.id, m), { hidePoints: meta.hidePoints === true });
    itemFiles[item.id] = rel;
    const reset = counterResets.get(item.id);
    return [...(reset !== undefined ? [`#ex-counter.update(${reset})`] : []), `#include ${str(rel)}`].join('\n');
  };

  const footBlocks: { itemId: string | null; snippet: string }[] = [];

  for (const item of doc.items) {
    // an exercise is held by its own file, which `exerciseSnippet` records
    if (item.kind !== 'exercise') itemFiles[item.id] = main;
    /*
     * A pinned block goes to the foot of the last page, as in the other builds. A pinned
     * page break means nothing there and is dropped.
     */
    if (item.pinToBottom) {
      let content: string | null = null;
      if (item.kind === 'text') content = textContent(item);
      else if (item.kind === 'heading') content = headingText(item) || null;
      else if (item.kind === 'exercise') content = exerciseSnippet(item);
      if (content) footBlocks.push({ itemId: item.id, snippet: `#block(breakable: false, width: 100%)[\n${content}\n]` });
      continue;
    }
    switch (item.kind) {
      case 'exercise':
        emit(item.id, exerciseSnippet(item));
        break;
      case 'heading': {
        // unnumbered: the headers number `=` headings, and a section title is not one
        const text = headingText(item);
        if (text) emit(item.id, `#heading(numbering: none)[${text}]`);
        break;
      }
      case 'text': {
        const content = textContent(item);
        if (content !== null) emit(item.id, content);
        break;
      }
      case 'pagebreak':
        emit(item.id, '#pagebreak(weak: true)');
        break;
    }
  }

  /*
   * The foot note: a separator titled like the kNobel teaser the course prints with
   * `knobel-task`, and the note under it. A sheet written in Typst carries its note as
   * Typst; the server hands over a LaTeX note compiled as an embed.
   */
  const note =
    input.footNote !== undefined
      ? input.footNote
      : meta.markup !== 'latex' && (meta.footNote ?? '').trim()
        ? { kind: 'typst' as const, body: meta.footNote! }
        : null;
  if (note) {
    let body: string | null = null;
    if (note.kind === 'typst') {
      const typst = tidy(toCourseBody(note.body));
      if (typst.trim()) {
        inlined.push(typst);
        body = typst;
      }
    } else {
      mainNeedsEmbed = true;
      body = `#studio-embed(${str(note.path)}, ${note.pages})`;
    }
    if (body) {
      const noteTitle = escapeTypstText((meta.footNoteTitle ?? '').trim() || 'Note');
      footBlocks.push({ itemId: null, snippet: `#block(breakable: false, width: 100%)[\n#separator[${noteTitle}]\n${body}\n]` });
    }
  }

  if (footBlocks.length) {
    // what is left of the last page, so the blocks sit at its foot (`\vfill` in LaTeX)
    lines.push('#v(1fr)');
    for (const block of footBlocks) emit(block.itemId, block.snippet);
  }

  /*
   * Inserted last, once it is known what the inlined bodies need: the helper and the
   * shim go between the import and the show rule, and every recorded line moves down.
   */
  const preamble: string[] = [];
  const shim = courseShim(inlined.join('\n'));
  if (shim) preamble.push(...shim.split('\n'));
  if (mainNeedsEmbed) preamble.push('', ...EMBED_HELPER.split('\n'));
  if (preamble.length) {
    lines.splice(preambleAt, 0, ...preamble);
    for (const range of Object.values(mainLines)) {
      range[0] += preamble.length;
      range[1] += preamble.length;
    }
  }

  while (lines.length && lines[lines.length - 1] === '') lines.pop();
  files[main] = lines.join('\n') + '\n';

  return { baseDir, dir, main, files, itemFiles, mainLines, buildNames, warnings };
}

/** The file of one exercise: its own import, the `#exercise` heading, and the body. */
function exerciseFile(
  item: SheetItem,
  entry: CourseItemInput | undefined,
  warn: (message: string) => void,
  opts: { hidePoints?: boolean } = {},
): string {
  const content: CourseItemInput['content'] = entry?.content ?? {
    kind: 'missing',
    reason: 'This exercise could not be found.',
  };
  const args: string[] = [];
  const points = entry?.points;
  if (opts.hidePoints) args.push('points: none');
  else if (typeof points === 'number' && Number.isFinite(points)) args.push(`points: ${points}`);
  const difficulty = entry?.difficulty;
  // as Studio's own template: nothing for 0 or a fraction, four camels for anything above
  if (typeof difficulty === 'number' && Number.isInteger(difficulty) && difficulty >= 1) {
    args.push(`difficulty: ${Math.min(difficulty, 4)}`);
  }
  const title = (entry?.title ?? '').replace(/\s+/g, ' ').trim();
  const archive = entry?.archive ?? null;
  const competencies = (archive?.competencies ?? []).filter((c) => c.trim());
  if (competencies.length) args.push(`competencies: (${competencies.map(str).join(', ')}${competencies.length === 1 ? ',' : ''})`);

  const head = [
    `// Generated by Sheet Studio — ${oneLine(item.exerciseId ?? 'written on the sheet')}`,
    '#import "/headers/lib.typ": *',
  ];
  /** comment lines right above the call */
  const before: string[] = [];
  if (archive) {
    head.push(
      COURSE_ARCHIVE_IMPORT,
      `// The Typst version of the 2026 archive: archive/2026/${oneLine(archive.path)}` +
        (archive.block > 1 ? `, exercise ${archive.block} of the file` : ''),
    );
    const number = (archive.number ?? '').trim();
    if (number) before.push(`// number: ${oneLine(number)} in 2025`);
  }
  /*
   * An archive body needs `compat.typ` whether or not its block is still found in the
   * index: the id is positional (`<path>#<n>`), and a snapshot that renamed the fragment
   * left the body without its import — `unknown variable: fit-width`. So the id itself
   * and the body's own use of a compat name decide too.
   */
  if (
    !archive &&
    content.kind === 'typst' &&
    ((item.exerciseId ?? '').startsWith('a26/') || archiveCompatUses(content.body).length)
  ) {
    head.push(COURSE_ARCHIVE_IMPORT);
  }
  let body: string;
  if (content.kind === 'typst') {
    body = tidy(toCourseBody(content.body, { includeSolution: entry?.includeSolution }));
    const shim = courseShim(body);
    if (shim) head.push(shim);
  } else if (content.kind === 'embed') {
    head.push('', EMBED_HELPER);
    const student = `studio-embed(${str(fromExercises(content.student.path))}, ${content.student.pages})`;
    const sol = entry?.includeSolution === false ? null : content.solution;
    /*
     * Two renders of the LaTeX source, picked at compile time: the headers' own flag
     * decides, so the student PDF never contains the solution pages at all. Marking
     * reads as solution here, which is what `is-solution()` answers for it.
     */
    body = sol
      ? [
          '#context if is-solution() {',
          `  studio-embed(${str(fromExercises(sol.path))}, ${sol.pages})`,
          '} else {',
          `  ${student}`,
          '}',
        ].join('\n')
      : `#${student}`;
  } else {
    warn(content.reason);
    body = missingText(content.reason);
  }

  // the title is the first positional argument, the body the last
  const markup = (entry?.titleMarkup ?? '').trim();
  const titleArg = markup ? `[${markup}]` : title ? `[${escapeTypstText(title)}]` : '';
  const call = `#exercise${args.length ? `(${args.join(', ')})` : ''}${titleArg}[`;
  /*
   * The body is not re-indented, unlike in Studio's own sheet: indentation is content
   * inside a raw block, and shifting it would change every code example.
   */
  return [...head, '', ...before, call, ...(body ? [body] : []), ']', ''].join('\n');
}
